import { faPen, faNetworkWired, faTrash } from '@fortawesome/free-solid-svg-icons';
import store from 'redux/store';
import { deleteIntegration } from 'redux/slices/lists/integrationsSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import { createInQuery } from 'actions/createInQuery';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';

type IntegrationEntity = Extract<ActionEntity, { type: 'integration' }>;

const isAllIntegration = (entities: readonly ActionEntity[]): entities is readonly IntegrationEntity[] =>
  entities.every((entity) => entity.type === 'integration');

/**
 * Action definitions for integrations — a route-based type: create / edit /
 * nodes navigate to full pages rather than opening a dialog, so they use the
 * `navigate` channel and report `'disabled'` when it's absent. `createIntegration`
 * / `addToTransportIntegration` are scoped; `edit` / `integrationNodes` /
 * `delete` are contextual.
 */
export const integrationActionDefinitions: ActionDefinitions = [
  {
    id: 'createIntegration',
    scope: 'type',
    label: 'New Integration',
    resolve: (ctx): ActionResult => {
      const { navigate, createIn } = ctx;
      return navigate ? () => navigate(`/configuration/integrations/new${createInQuery(createIn)}`) : 'disabled';
    },
  },
  {
    id: 'edit',
    label: 'Edit Integration',
    icon: faPen,
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllIntegration(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () => navigate(`/configuration/integrations/${only.id}/edit`);
    },
  },
  {
    id: 'integrationNodes',
    label: 'Integration Nodes',
    icon: faNetworkWired,
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllIntegration(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () => navigate(`/configuration/integrations/${only.id}/nodes`);
    },
  },
  {
    id: 'delete',
    label: 'Remove',
    icon: faTrash,
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllIntegration(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteIntegration(entity.id)), {
        refresh,
        confirmBody: 'Are you sure you want to delete this integration? This will also delete the linked folder. All integration nodes must be deleted first.',
      });
    },
  },
  {
    id: 'addToTransportIntegration',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllIntegration(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'integration',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.name : 'Selected integrations' }
      );
    },
  },
];
